<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'

import { DateTime } from 'luxon'

import DateTimePicker from '../../../common/components/inputs/DateTimePicker.vue'
import ShiftOfferStatusBadge from '../../../shifts/components/badges/ShiftOfferStatusBadge.vue'
import ShiftOfferActions from '../actions/ShiftOfferActions.vue'

import {
  useShiftCheckInDeleteMutation,
  useShiftCheckInMutation,
  useShiftCheckOutDeleteMutation,
  useShiftCheckOutMutation,
} from '../../../shift_check_ins/composables/useShiftCheckIn'

import { EmployeeShiftOffer } from '../../../employee_shift/models/employeeShiftOffer'
import { EmployeeID } from '../../../employees/models/employee'
import { Shift } from '../../../shifts/models/shift'
import { SiteID } from '../../../sites/models/site'

const { siteId, shift, offer, field } = defineProps<{
  siteId: SiteID
  shift: Shift
  offer: EmployeeShiftOffer
  field: string
}>()

const checkInForm = ref<{
  timestamp: DateTime | null
  latitude: number | null
  longitude: number | null
  onBehalfOfEmployeeId: EmployeeID | null
}>({
  timestamp: null,
  latitude: null,
  longitude: null,
  onBehalfOfEmployeeId: null,
})

const hasCheckIn = computed(() => !!offer.checkedInAt)
const isLateForCheckIn = computed(() => {
  if (offer.checkedInAt) {
    return false
  }

  return shift.startDate < DateTime.now()
})
const wasCheckInLate = computed(() => {
  if (!offer.checkedInAt) {
    return false
  }

  return offer.checkedInAt > shift.startDate
})

const hasCheckOut = computed(() => !!offer.checkedOutAt)
const isLateForCheckOut = computed(() => {
  if (offer.checkedOutAt) {
    return false
  }

  return shift.endDate < DateTime.now()
})
const wasCheckOutLate = computed(() => {
  if (!offer.checkedOutAt) {
    return false
  }

  return offer.checkedOutAt < shift.endDate
})

const checkInPopover = useTemplateRef('check-in-popover')
const toggleCheckInPopover = (event: Event) =>
  checkInPopover.value?.toggle(event)

const { mutate: checkInMutate, isPending: isCheckInPending } =
  useShiftCheckInMutation()
const checkIn = () => {
  checkInMutate(
    {
      siteId,
      shiftId: shift.id,
      form: {
        timestamp: checkInForm.value.timestamp,
        latitude: checkInForm.value?.latitude ?? null,
        longitude: checkInForm.value?.longitude ?? null,
        onBehalfOfEmployeeId:
          checkInForm.value?.onBehalfOfEmployeeId ?? offer.employee?.id,
      },
    },
    {
      onSuccess: () => {
        checkInPopover.value?.hide()
      },
    },
  )
}

const checkOutPopover = useTemplateRef('check-out-popover')
const toggleCheckOutPopover = (event: Event) =>
  checkOutPopover.value?.toggle(event)
const { mutate: checkOutMutate, isPending: isCheckOutPending } =
  useShiftCheckOutMutation()
const checkOut = () => {
  checkOutMutate(
    {
      siteId,
      shiftId: shift.id,
      form: {
        timestamp: checkInForm.value?.timestamp ?? DateTime.now(),
        latitude: checkInForm.value?.latitude ?? null,
        longitude: checkInForm.value?.longitude ?? null,
        onBehalfOfEmployeeId:
          checkInForm.value?.onBehalfOfEmployeeId ?? offer.employee?.id,
      },
    },
    {
      onSuccess: () => {
        checkOutPopover.value?.hide()
      },
    },
  )
}

const { mutate: deleteCheckInMutate, isPending: isDeleteCheckInPending } =
  useShiftCheckInDeleteMutation()
const deleteCheckIn = () => {
  deleteCheckInMutate(
    {
      siteId,
      shiftId: shift.id,
      onBehalfOfEmployeeId: offer.employee?.id,
    },
    {
      onSuccess: () => {
        checkInPopover.value?.hide()
      },
    },
  )
}

const { mutate: deleteCheckOutMutate, isPending: isDeleteCheckOutPending } =
  useShiftCheckOutDeleteMutation()
const deleteCheckOutPressed = () => {
  deleteCheckOutMutate(
    {
      siteId,
      shiftId: shift.id,
      onBehalfOfEmployeeId: offer.employee?.id,
    },
    {
      onSuccess: () => {
        checkOutPopover.value?.hide()
      },
    },
  )
}
</script>

<template>
  <div>
    <template v-if="field === 'employee'">
      <div class="flex items-center">
        <Avatar
          :label="
            (offer.employee?.firstName.charAt(0) ?? '') +
            (offer.employee?.lastName.charAt(0) ?? '')
          "
          :image="offer.employee?.avatarUrl"
          class="bg-highlight"
          shape="circle"
        />
        <div class="ml-4">
          <div class="font-medium">
            {{ offer.employee?.firstName }} {{ offer.employee?.lastName }}
            <Tag
              v-if="!offer.employee?.hasAccount"
              v-tooltip="
                'This user does not have an account yet. They can be scheduled for shifts but will not have access to CliniShift.'
              "
              icon="pi pi-user"
              severity="danger"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="field === 'offerStatus'">
      <div :class="{ hidden: offer.status !== 'queued' }">
        <ShiftOfferStatusBadge
          :status="offer.status"
          :queue-order="offer.queueOrder"
          :queue-at="offer.queueProcessOn"
        />
      </div>
    </template>

    <template v-else-if="field === 'checkIn'">
      <div
        v-if="offer.status === 'accepted'"
        class="space-x-1 flex items-center"
      >
        <Tag
          v-if="hasCheckIn"
          v-tooltip="wasCheckInLate ? 'This check-in was late.' : ''"
          :value="offer.checkedInAt?.toLocaleString(DateTime.DATETIME_MED)"
          :icon="wasCheckInLate ? 'pi pi-exclamation-triangle' : undefined"
          :severity="wasCheckInLate ? 'warn' : undefined"
        />

        <Button
          v-if="offer.checkedInAt"
          v-tooltip="'Clear the check-in information.'"
          icon="pi pi-times"
          size="small"
          severity="danger"
          outlined
          :loading="isDeleteCheckInPending"
          @click="deleteCheckIn"
        />

        <Button
          v-if="!offer.checkedInAt && isLateForCheckIn"
          v-tooltip="'Manually check-in on behalf of employee.'"
          icon="pi pi-clock"
          label="No Check-In"
          size="small"
          severity="danger"
          outlined
          @click="toggleCheckInPopover"
        />

        <Popover ref="check-in-popover">
          <div class="flex flex-col">
            <DateTimePicker
              v-model:selected="checkInForm.timestamp"
              class="flex-1"
              show-time
              placeholder="Check-In Time"
            />

            <Button
              label="Confirm"
              class="mt-2"
              :loading="isCheckInPending"
              @click="checkIn"
            />
          </div>
        </Popover>

        <Tag
          v-if="hasCheckOut"
          v-tooltip="wasCheckOutLate ? 'This check-out was early.' : ''"
          :value="offer.checkedOutAt?.toLocaleString(DateTime.DATETIME_MED)"
          :icon="wasCheckOutLate ? 'pi pi-exclamation-triangle' : undefined"
          :severity="wasCheckOutLate ? 'warn' : undefined"
          @click="toggleCheckOutPopover"
        />

        <Button
          v-if="offer.checkedOutAt"
          v-tooltip="'Clear the check-out information.'"
          icon="pi pi-times"
          size="small"
          severity="danger"
          outlined
          :loading="isDeleteCheckOutPending"
          @click="deleteCheckOutPressed"
        />

        <Button
          v-if="!hasCheckOut && isLateForCheckOut"
          v-tooltip="'Manually check-out on behalf of employee.'"
          icon="pi pi-clock"
          label="No Check-Out"
          size="small"
          severity="danger"
          outlined
          @click="toggleCheckOutPopover"
        />

        <Popover ref="check-out-popover">
          <div class="flex flex-col">
            <DateTimePicker
              v-model:selected="checkInForm.timestamp"
              class="flex-1"
              show-time
              placeholder="Check-Out Time"
            />

            <Button
              label="Confirm"
              class="mt-2"
              :loading="isCheckOutPending"
              @click="checkOut"
            />
          </div>
        </Popover>
      </div>
    </template>

    <template v-else-if="field === 'actions'">
      <ShiftOfferActions :site-id="siteId" :shift="shift" :offer="offer" />
    </template>
  </div>
</template>
